import cookies from 'js-cookie';
import { create } from 'zustand';

import { CompanyRequest, CompanyResponse, Employee, PatchedCompanyRequest, PatchedEmployeesPatch } from '@/libs/api';
import apiClient from '@/libs/api/client';

import { runTask } from './modal';
import { AuthResult, COOKIE_AUTH_NAME, useUserStore } from './user';

if (!process.env.REACT_APP_COOKIE_EXPIRE_DAY) {
  throw new Error('REACT_APP_COOKIE_EXPIRE_DAY not found');
}

const cookieExpireDay = parseInt(process.env.REACT_APP_COOKIE_EXPIRE_DAY);

type CompanyState = {
  company: Partial<CompanyResponse>;
  employee: Employee | null;
  isSuccess: boolean;
  createCompany: (arg: FormData) => Promise<boolean>;
  getCompany: () => Promise<CompanyResponse | null>;
  updateCompany: (companyData: FormData) => Promise<boolean>;
  getProfileId: () => number | null;
  getCompanyEmployee: () => void;
  updateCompanyEmployee: (data: PatchedEmployeesPatch) => Promise<boolean>;
};

export const useCompanyStore = create<CompanyState>((set, get) => ({
  company: {},
  employee: null,
  isSuccess: false,
  createCompany: async (arg: FormData) => {
    let isSuccess = false;

    await runTask(async () => {
      const data = arg as unknown as CompanyRequest;
      const company = await apiClient.company.companyCreate(data);
      isSuccess = true;

      const authCookies = useUserStore.getState().getCookies();
      if (authCookies) {
        authCookies.companyId = company.id;
        cookies.set(COOKIE_AUTH_NAME, JSON.stringify(authCookies), {
          expires: cookieExpireDay
        });
      }
      set({ company, isSuccess });
    });

    return isSuccess;
  },
  getCompany: async () => {
    const companyId = useUserStore.getState().getCompanyIdUserBelongTo();
    let company: CompanyResponse | null = null;

    if (companyId) {
      await runTask(async () => {
        company = await apiClient.company.companyRetrieve(companyId);
        set({ company });
      });
    }
    return company;
  },
  updateCompany: async (companyData: FormData) => {
    let isSuccess = false;
    const companyId = useUserStore.getState().getCompanyIdUserBelongTo();

    if (companyId) {
      await runTask(async () => {
        const data = companyData as PatchedCompanyRequest;
        const company = await apiClient.company.companyPartialUpdate(companyId, data);
        isSuccess = true;
        set({ company, isSuccess });
      });
    }
    return isSuccess;
  },
  getProfileId: () => {
    const cookie = cookies.get(COOKIE_AUTH_NAME);
    if (cookie) {
      const result = JSON.parse(cookie) as AuthResult;
      if (result?.profileId) {
        return result?.profileId;
      }
    }
    return null;
  },
  async getCompanyEmployee() {
    const profileId = get().getProfileId();
    if (profileId) {
      await runTask(async () => {
        const employee = await apiClient.company.companyEmployeeRetrieve(profileId);
        set({ employee });
      });
    }
  },
  async updateCompanyEmployee(data: PatchedEmployeesPatch) {
    let isSuccess = false;
    const profileId = get().getProfileId();
    if (profileId) {
      const formData = new FormData();

      Object.keys(data).forEach((k) => {
        const key = k as keyof typeof data;
        const value = data[key];
        if (key && value) {
          formData.append(key, value.toString());
        }
      });

      await runTask(async () => {
        const employee = await apiClient.company.companyEmployeePartialUpdate(
          profileId,
          formData as PatchedEmployeesPatch
        );
        set({ employee });
        isSuccess = true;
      });
    }
    return isSuccess;
  }
}));
